/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from "react";
import {
  FaEdit,
  FaTrash,
  FaRegSquare,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import {
  fetchTaskLogsForDate,
  fetchTaskTemplates,
  toggleChecklistItem,
  generateTaskLogsForRange,
} from "../services/taskService";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { doc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";

const EVENT_LABELS = [
  "Upon waking",
  "Before breakfast",
  "During breakfast",
  "After breakfast",
  "Before lunch",
  "During lunch",
  "After lunch",
  "Before dinner",
  "During dinner",
  "After dinner",
  "At bedtime",
];

const TasksPage = () => {
  const { activeCareProfile, currentUser } = useAuth();
  const [dateStr, setDateStr] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingLog, setEditingLog] = useState(null); // task log object being edited
  const [editChecklist, setEditChecklist] = useState([""]); // sentinel pattern
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");

  // State for generate-tasks modal
  const [showGenModal, setShowGenModal] = useState(false);
  const [genTemplates, setGenTemplates] = useState([]); // templates applicable to date
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [genLoading, setGenLoading] = useState(false);
  const [rangeStart, setRangeStart] = useState(() => new Date().toISOString().split("T")[0]);
  const [rangeEnd, setRangeEnd] = useState(() => new Date().toISOString().split("T")[0]);

  // Removed per range generation; templates are filtered day-by-day at generation time

  const openGenModal = async () => {
    if (!activeCareProfile) return;
    setGenLoading(true);
    try {
      const templates = await fetchTaskTemplates(activeCareProfile.id);
      setGenTemplates(templates);
      setSelectedIds(new Set(templates.map((t) => t.id)));
      setShowGenModal(true);
      setRangeStart(dateStr);
      setRangeEnd(dateStr);
    } catch (err) {
      console.error(err);
      alert("Failed to load instructions: " + err.message);
    } finally {
      setGenLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const allSelected = genTemplates.length > 0 && selectedIds.size === genTemplates.length;

  const toggleSelectAll = () => {
    setSelectedIds((_prev) => {
      if (allSelected) return new Set();
      return new Set(genTemplates.map((t) => t.id));
    });
  };

  const generateSelected = async () => {
    if (!activeCareProfile || !currentUser) return;
    const selectedTemplates = genTemplates.filter((t) => selectedIds.has(t.id));
    if (selectedTemplates.length === 0) {
      alert("Please select at least one instruction to generate.");
      return;
    }
    // Validate range and max 30 days forward
    const start = new Date(rangeStart + "T00:00:00");
    const end = new Date(rangeEnd + "T00:00:00");
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      alert("Please select valid start and end dates.");
      return;
    }
    if (end < start) {
      alert("End date must be on or after start date.");
      return;
    }
    const maxEnd = new Date(start);
    maxEnd.setDate(maxEnd.getDate() + 29);
    if (end > maxEnd) {
      alert("Please select a range of at most 30 days.");
      return;
    }
    setGenerating(true);
    try {
      // Generate across the range using the new service
      await generateTaskLogsForRange({
        careProfileId: activeCareProfile.id,
        startDate: start,
        endDate: end,
        currentUser,
      });
      setShowGenModal(false);
      await loadLogs();
    } catch (err) {
      console.error(err);
      alert("Failed: " + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const loadLogs = async () => {
    if (!activeCareProfile) return;
    setLoading(true);
    try {
      const data = await fetchTaskLogsForDate(activeCareProfile.id, dateStr);
      setLogs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCareProfile, dateStr]);

  const handleToggle = async (logId, idx, completed) => {
    try {
      await toggleChecklistItem({
        careProfileId: activeCareProfile.id,
        logId,
        itemIndex: idx,
        completed,
        currentUser,
      });
      await loadLogs();
    } catch (err) {
      console.error(err);
      alert("Update failed: " + err.message);
    }
  };

  const groupLogs = () => {
    const timeLogs = logs.filter((l) => l.scheduleSnapshot.type === "time");
    const eventLogs = logs.filter((l) => l.scheduleSnapshot.type === "event");
    timeLogs.sort((a, b) => (a.scheduleSnapshot.time > b.scheduleSnapshot.time ? 1 : -1));
    // order by predefined label list
    eventLogs.sort(
      (a, b) =>
        EVENT_LABELS.indexOf(a.scheduleSnapshot.eventLabel) -
        EVENT_LABELS.indexOf(b.scheduleSnapshot.eventLabel),
    );
    return [...timeLogs, ...eventLogs];
  };

  const startEdit = (log) => {
    setEditingLog(log);
    setEditTitle(log.title);
    setEditDesc(log.description || "");
    setEditChecklist(
      Array.isArray(log.checklist) && log.checklist.length > 0
        ? [...log.checklist.map((c) => c.text), ""]
        : [""],
    );
  };

  const saveEdit = async () => {
    if (!activeCareProfile || !currentUser || !editingLog) return;
    const cleaned = editChecklist.map((t) => t.trim()).filter(Boolean);
    const newChecklist = cleaned.map((text) => ({
      text,
      completed: false,
      completedByUid: null,
      completedAt: null,
    }));
    try {
      await updateDoc(
        doc(
          db,
          `careProfiles/${activeCareProfile.id}/taskLogs/${editingLog.id}`,
        ),
        {
          title: editTitle,
          description: editDesc,
          checklist: newChecklist,
          lastModifiedBy: {
            uid: currentUser.uid,
            name: currentUser.displayName || currentUser.email || "",
          },
          lastModifiedAt: Timestamp.now(),
        },
      );
      setEditingLog(null);
      await loadLogs();
    } catch (err) {
      console.error(err);
      alert("Failed to save edits: " + err.message);
    }
  };

  const canDelete = activeCareProfile && currentUser && activeCareProfile.userId === currentUser.uid;

  const deleteLog = async (log) => {
    if (!canDelete) return alert("Only profile owner can delete tasks.");
    if (!window.confirm("Delete this task log?") ) return;
    try {
      await deleteDoc(
        doc(db, `careProfiles/${activeCareProfile.id}/taskLogs/${log.id}`),
      );
      await loadLogs();
    } catch (err) {
      console.error(err);
      alert("Delete failed: " + err.message);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <input
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          className="border px-3 py-2 rounded"
        />
        <button
          onClick={openGenModal}
          disabled={genLoading || generating}
          className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
        >
          {genLoading ? "Loading..." : generating ? "Generating..." : "Generate tasks"}
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : logs.length === 0 ? (
        <p>No tasks for this date.</p>
      ) : (
        <div className="space-y-6">
          {groupLogs().map((log) => {
            const total = Array.isArray(log.checklist) ? log.checklist.length : 0;
            const completed = Array.isArray(log.checklist)
              ? log.checklist.filter((c) => c.completed).length
              : 0;

            // Build schedule context string
            const ss = log.scheduleSnapshot || {};
            let scheduleStr = "";
            if (ss.type === "time" && ss.time) {
              try {
                const dateObj = new Date(`1970-01-01T${ss.time}:00`);
                scheduleStr = dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
              } catch (_) {
                scheduleStr = ss.time;
              }
            } else if (ss.type === "event" && ss.eventLabel) {
              scheduleStr = ss.eventLabel;
            }
            if (ss.repeat === "weekly" && Array.isArray(ss.daysOfWeek) && ss.daysOfWeek.length) {
              scheduleStr += ` · ${ss.daysOfWeek.join(", ")}`;
            } else if (ss.repeat === "daily") {
              scheduleStr += " · Daily";
            }
            return (
              <div
                key={log.id}
                className="relative bg-white p-4 pb-8 rounded shadow border-l-4 border-green-500"
              >
                <div className="w-full flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{log.title}</h3>
                    <p className="text-sm text-gray-600">{log.description}</p>
                    {scheduleStr && <p className="text-xs text-gray-500 mt-1">{scheduleStr}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                      title={expandedId === log.id ? "Collapse" : "Expand"}
                    >
                      {expandedId === log.id ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    <button
                      onClick={() => startEdit(log)}
                      className="p-1 text-yellow-600 hover:text-yellow-800"
                      title="Edit"
                    >
                      <FaEdit size={14} />
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => deleteLog(log)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FaTrash size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {expandedId === log.id && (
                  <ul className="space-y-1 mt-3">
                    {(Array.isArray(log.checklist) ? log.checklist : []).map((item, idx) => (
                      <li key={idx} className="flex justify-between items-center gap-2">
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            onClick={(e)=>e.stopPropagation()}
                            type="checkbox"
                            checked={item.completed}
                            onChange={() => handleToggle(log.id, idx, !item.completed)}
                          />
                          <span className={item.completed ? "line-through text-gray-500" : ""}>{item.text}</span>
                        </div>
                        {item.completed && item.completedAt && (
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {(
                              item.completedAt.toDate
                                ? item.completedAt.toDate()
                                : new Date(item.completedAt)
                            ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {total > 0 && (
                  <span className="absolute bottom-3 right-3 text-xs text-gray-500">
                    {completed}/{total}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {editingLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-full max-w-lg max-h-[90vh] overflow-auto">
            <h3 className="text-lg font-semibold mb-2">Edit Task</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows="3"
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Checklist</label>
                <div className="space-y-2">
                  {editChecklist.map((item, idx) => {
                    const isLast = idx === editChecklist.length - 1;
                    return (
                      <div key={idx} className="flex items-center gap-2 group">
                        <FaRegSquare className="text-gray-400" />
                        <input
                          type="text"
                          value={item}
                          placeholder={isLast ? "Add option" : `Item ${idx + 1}`}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditChecklist((prev) => {
                              const arr = [...prev];
                              arr[idx] = val;
                              if (isLast && val.trim() !== "") arr.push("");
                              return arr;
                            });
                          }}
                          className="flex-1 border-b focus:outline-none px-1 py-1"
                        />
                        {!isLast && (
                          <button
                            type="button"
                            onClick={() => setEditChecklist((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-gray-400 opacity-0 group-hover:opacity-100"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-4">
              <button
                onClick={() => setEditingLog(null)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {showGenModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-full max-w-md max-h-[90vh] overflow-auto">
            <h3 className="text-lg font-semibold mb-2">Generate Tasks</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Start date</label>
                  <input type="date" value={rangeStart} onChange={(e)=>setRangeStart(e.target.value)} className="w-full border px-3 py-2 rounded" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End date</label>
                  <input type="date" value={rangeEnd} onChange={(e)=>setRangeEnd(e.target.value)} className="w-full border px-3 py-2 rounded" />
                </div>
              </div>
              {genTemplates.length === 0 ? (
                <p className="text-sm text-gray-600">No instructions exist yet. Create one first under the Instructions tab.</p>
              ) : (
                <>
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                  <span className="font-medium">Select All</span>
                </div>
                {genTemplates.map((tpl) => {
                  const exists = logs.some((l) => l.templateId === tpl.id);
                  const disabled = exists;
                  const checked = selectedIds.has(tpl.id);
                  return (
                    <div key={tpl.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggleSelect(tpl.id)}
                      />
                      <span className={disabled ? "text-gray-400" : ""}>{tpl.title}</span>
                      {disabled && <span className="text-xs text-gray-400">(already exists)</span>}
                    </div>
                  );
                })}
                </>
              )}
            <div className="flex justify-end gap-4 mt-4">
              <button onClick={() => setShowGenModal(false)} className="px-4 py-2 bg-gray-200 rounded">
                Cancel
              </button>
              <button
                onClick={generateSelected}
                disabled={generating || selectedIds.size === 0}
                className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
              >
                {generating ? "Generating..." : "Generate"}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
